import {
  FC,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { Route, Navigate, useLocation, useMatch } from 'react-router-dom';

import { LegacyAppProvider } from '@/app';
import ErrorCard from '@/components/error-card/new-error-card';
import Loader from '@/components/loader';
import { APP_ROUTE, ONBOARDING } from '@/constants';
import { useIsAuthenticated, useSignOut } from '@/hooks/api/auth';
import { useAcceptInvite } from '@/hooks/api/team';
import { AddonContainer, BillingContainer, UserContainer } from '@/providers';
import { ProviderWrapper } from '@/providers/helpers/provider-wrapper';
import { useSession } from '@/states/common';
import { Business } from '@/views/settings/account/invitations';

import { usePatchUser } from '@/hooks/api/user';
import API from '@/api';
import SubscriptionChange from '../subscription-change-modal';
import { isValidPage } from './utils';
import {
  getAuthOnlyCallbackUrl,
  resolvePostLoginPath,
} from './post-login-path';

export {
  getAuthOnlyCallbackUrl,
  resolvePostLoginPath,
  sanitizeCallbackUrl,
} from './post-login-path';

const onboardingUrlList = [
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.INVITED}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.VERIFICATION}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.WELCOME}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.PROFILE}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.BUSINESS}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.ADD_CLIENT}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}/${APP_ROUTE.FORGOT_PASSWORD}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}/${APP_ROUTE.FORGOT_PASSWORD}/success`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.SIGN_UP}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.SIGN_UP}/${APP_ROUTE.INVITED}`,
  // V2
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.INVITED}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.VERIFICATION}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.WELCOME}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.PROFILE}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.BUSINESS}`,
  `/${APP_ROUTE.ONBOARDING}/${APP_ROUTE.ADD_CLIENT}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}/${APP_ROUTE.FORGOT_PASSWORD}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}/${APP_ROUTE.FORGOT_PASSWORD}/success`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.SIGN_UP}`,
  `/${APP_ROUTE.AUTH}/${APP_ROUTE.SIGN_UP}/${APP_ROUTE.INVITED}`,
];

type ViewMetricsRouteProps = PropsWithChildren<{
  path?: string | string[];
  element?: ReactElement;
  isAuthenticationRequired?: boolean;
  restrictedPlans?: string[];
}>;

const ViewMetricsRoute: FC<ViewMetricsRouteProps> = ({
  element,
  path,
  isAuthenticationRequired = true,
  restrictedPlans = [],
  children,
  ...rest
}) => {
  const match = useMatch('*');
  const location = useLocation();
  const [cookies] = useCookies();
  const isAuthenticated = useIsAuthenticated();
  const acceptInvite = useAcceptInvite();
  const signout = useSignOut();

  const compatPath = match ? match.pathname : path;

  const { isAddon } = AddonContainer.useContainer();
  const { state: userState, setRedirectURL } = UserContainer.useContainer();
  const { state: billingState } = BillingContainer.useContainer();

  const [redirectPath, setRedirectPath] = useState('');
  const plan = userState.business?.billing?.subscriptionName;
  const isRouteRestricted = restrictedPlans.includes(plan);
  const subscriptionStatus = userState?.business?.billing?.status ?? '';

  const onboardingStatus = userState.user?.onboarding;

  /**
   *
   * This useEffect will save the user request url to userState
   * and if login it will be redirect to this url
   *
   */
  useEffect(() => {
    if (
      !isAuthenticated() &&
      location.pathname !== `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}` &&
      isValidPage(location.pathname)
    ) {
      setRedirectURL(location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *
   * @shorthand iff stands for `if and only if`
   * @function: Function wrapper used to trigger accept invite hook
   * iff user is coming via mail
   *
   * @param id: Access Id of the business to which user was invited
   * @param businessId: Business Id required in the backend to verify if the access exists
   *
   */

  const handleAutoAcceptInvite = useCallback(
    async (id: string, businessId: string): Promise<void> => {
      await acceptInvite(id, businessId);
    },
    [acceptInvite]
  );

  /**
   *
   * This hook is used to auto accept the invitation for joining a business
   * iff user is coming via email link then only this will hook will call the
   * @function handleAutoAcceptInvite to make an api call to accept the user invite
   *
   */

  useEffect(() => {
    if (userState?.businessOptions !== null && userState?.accessId !== null) {
      const invites: {
        name: string;
        id: string;
        businessId: string;
      }[] = userState.businessOptions
        .filter(({ access }: Business) => {
          return access.acceptedInviteDate == null && !access.isPrimary;
        })
        .map(({ access, name }: Business) => ({
          name,
          id: access?.id,
          businessId: access.businessId,
        }));
      const toAutoAcceptInvite = invites.filter(
        ({ id }) => id.toString() === userState.accessId
      );
      if (toAutoAcceptInvite.length > 0) {
        handleAutoAcceptInvite(
          toAutoAcceptInvite[0].id,
          toAutoAcceptInvite[0].businessId
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState?.businessOptions]);

  useEffect(
    function handleLogin() {
      if (isAuthenticated() && !isAddon) {
        /**
         * ANONYMOUS PATHS
         *
         * This case, user has authenticated but attemps to access anonymous
         * pages like update password etc.
         */
        if (
          compatPath?.includes(
            `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}/${APP_ROUTE.UPDATE_PASSWORD}`
          )
        ) {
          signout(false);
          return;
        }

        /**
         * UNVERIFIED USERS
         */
        if (
          !userState.user.isVerified &&
          userState.user.authMethods?.google == null
        ) {
          setRedirectPath(`/${APP_ROUTE.AUTH}/${APP_ROUTE.VERIFICATION}`);
          return;
        }

        const redirectToCallbackURL = () => {
          if (userState.redirectURL) {
            setRedirectPath(userState.redirectURL);
            setRedirectURL(null); // clean up after use to avoid infinite redirect
            return true;
          }
          return false;
        };

        /**
         * UN-PROTECTED PATHS
         *
         * This case, user has authenticated but attemps to access un-protected
         * pages like login, signup etc.
         */
        if (compatPath?.includes(`/${APP_ROUTE.AUTH}`)) {
          if (redirectToCallbackURL()) {
            return;
          }
          // Get the default client ID for the workspace path
          const defaultClientId = userState.client?.id;
          if (defaultClientId) {
            setRedirectPath(`/ws/${defaultClientId}/${APP_ROUTE.HOME}`);
          } else {
            setRedirectPath(`/${APP_ROUTE.HOME}`);
          }
          return;
        }

        // /**
        //  * RESTRICTED PATHS DUE TO PLAN RESTRICTIONS
        //  */
        // if (isRouteRestricted && restrictedPlans.length === 0) {
        //   setRedirectPath(`/${APP_ROUTE.HOME}`);
        //   return;
        // }

        /**
         * ONBOARDING PATHS
         */
        if (onboardingStatus !== ONBOARDING.COMPLETE_STEP) {
          setRedirectPath(`/${APP_ROUTE.ONBOARDING}`);
          return;
        }

        /**
         * CALLBACK REDIRECT URL (optional)
         */
        redirectToCallbackURL();
      } else if (isAuthenticationRequired) {
        setRedirectPath(`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userState.user]
  );

  if (
    subscriptionStatus === 'cancelled' &&
    location.pathname !== `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`
  ) {
    return (
      <SubscriptionChange
        open={billingState.showModal || subscriptionStatus === 'cancelled'}
        fromOnboarding={false}
        subscriptionCancelled
      />
    );
  }

  /**
   * COMPATIBILITY CODE
   */
  if (children) {
    /** PROTECTED PATHS */
    if (
      !cookies.accessToken &&
      isAuthenticationRequired &&
      location.pathname !== `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`
    ) {
      return <Navigate to={`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`} />;
    }

    /** CALLBACK REDIRECT URL (optional) */
    if (redirectPath !== '' && location.pathname !== redirectPath) {
      return <Navigate to={redirectPath} />;
    }

    return children;
  }
  /**
   * END OF COMPATIBILITY CODE
   */

  let renderElement = element;

  if (
    !cookies.accessToken &&
    isAuthenticationRequired &&
    location.pathname !== `/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`
  ) {
    renderElement = <Navigate to={`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`} />;
  } else if (redirectPath !== '' && location.pathname !== redirectPath) {
    renderElement = <Navigate to={redirectPath} />;
  }
  return <Route path={compatPath as string} element={renderElement} />;
};

const AuthContext = createContext<{
  isAuthenticated: boolean;
  isLoading: boolean;
} | null>(null);

const useAuth = (): {
  isAuthenticated: boolean;
  isLoading: boolean;
} => {
  const state = useContext(AuthContext);
  if (!state) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return state;
};

// Root provider to handle auth state
const AuthProvider = ({ children }: PropsWithChildren) => {
  const [cookies] = useCookies(['accessToken']);
  const [isLoading] = useState(false);
  const isAuthenticated = Boolean(cookies.accessToken);
  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Prepopulate user, business, client, clients state into legacy providers
 * @param param0
 * @returns
 */
export const LegacySessionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const query = useSession();
  const [cookies] = useCookies();

  const {
    user,
    business,
    client,
    clients,
    permission,
    businessOptions,
    paymentPlans,
    pages,
  } = query?.data ?? {};

  return (
    <ProviderWrapper
      initialState={{
        userState: {
          user,
          business,
          permission,
          businessOptions,
          paymentPlans,
        },
        clientState: {
          clients,
          selectedClient: client,
        },
        pagesState: {
          pages,
          isPagesPopulated: true,
        },
        authenticationState: {
          accessToken: cookies.accessToken,
        },
      }}
    >
      <LegacyAppProvider>{children}</LegacyAppProvider>
    </ProviderWrapper>
  );
};

const AuthOnly = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <Loader show />;

  if (!isAuthenticated) {
    return <Navigate to={`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`} replace />;
  }

  return <LegacySessionProvider>{children}</LegacySessionProvider>;
};

/**
 * Onboarding is complete but the account has no workspace to open. Shown
 * instead of a spinner so the user can get out (RCA 2026-08-29).
 */
const NoWorkspaceAvailable = () => {
  const signout = useSignOut();
  return (
    <ErrorCard
      errorMessage="No workspace is available for this account yet. Ask a business owner to add you to a workspace, or sign out and try another account."
      CTAText="Sign out"
      CTAAction={() => signout(true)}
    />
  );
};

const RedirectToWorkspace = () => {
  const query = useSession();
  const [cookies, , removeCookie] = useCookies(['callbackUrl']);

  // Resolve the destination exactly once. Honours the `callbackUrl` cookie
  // (set by flows that bounce an unauthenticated user through login, e.g.
  // /mcp/authorize); the cookie is consumed below so it can never be replayed
  // into a later login. Computing in a state initialiser means the removal
  // re-render cannot change `to` mid-navigation.
  const [to] = useState(() => resolvePostLoginPath(query.data, cookies));
  const hadCallbackCookie = typeof cookies.callbackUrl === 'string';

  useEffect(() => {
    if (hadCallbackCookie) {
      removeCookie('callbackUrl', { path: APP_ROUTE.ROOT });
    }
  }, [hadCallbackCookie, removeCookie]);

  if (to === null) return <NoWorkspaceAvailable />;
  return <Navigate to={to} replace />;
};

const AnonymousOnly = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const isAddon = window.location.pathname.includes(`/${APP_ROUTE.ADDON}`);

  if (isLoading) return <Loader show />;

  if (isAddon && isAuthenticated) {
    return children;
  }

  if (isAuthenticated) {
    return <RedirectToWorkspace />;
  }

  return children;
};

const OnboardingOnly = ({ children }: { children: ReactNode }) => {
  const { isLoading } = useAuth();

  const query = useSession();

  const { user } = query?.data ?? {};
  const onboardingStatus = user?.onboarding;
  if (isLoading) return <Loader show />;

  if (onboardingStatus !== ONBOARDING.COMPLETE_STEP) {
    return <Navigate to={`/${APP_ROUTE.ONBOARDING}`} replace />;
  }

  return children;
};

const NotOnboardingOnly = ({ children }: { children: ReactNode }) => {
  const { isLoading } = useAuth();
  const query = useSession();
  const { client } = query?.data ?? {};
  const [cookies] = useCookies();
  const callbackUrl = getAuthOnlyCallbackUrl(cookies, client?.id);

  const { user } = query?.data ?? {};

  const onboardingStatus = user?.onboarding;

  if (isLoading) return <Loader show />;

  if (onboardingStatus === ONBOARDING.COMPLETE_STEP) {
    return (
      <Navigate
        to={
          callbackUrl.startsWith(`/${APP_ROUTE.ONBOARDING}`)
            ? `/ws/${client?.id}/${APP_ROUTE.HOME}`
            : callbackUrl
        }
        replace
      />
    );
  }

  return children;
};

const SilentLogout = ({ children }: { children: ReactNode }) => {
  const { isLoading, isAuthenticated } = useAuth();
  const signout = useSignOut();

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      // don't redirect when logging out
      signout(false);
    }
  }, [isLoading, isAuthenticated, signout]);

  /**
   * Don't allow authenticated user to proceed further
   */
  if (isLoading || isAuthenticated) return <Loader show />;

  return children;
};

export {
  AnonymousOnly,
  AuthOnly,
  AuthProvider,
  NotOnboardingOnly,
  OnboardingOnly,
  SilentLogout,
};
export default ViewMetricsRoute;
